import { useMemo, useRef } from 'react';
import { safeParseConfig } from '../../exercise-templates/blockConfig';
import { MATCHING_DEFAULT_CONFIG, MATCHING_MODE_DEFERRED, getMatchingMode, normalizePairs, isSideFilled } from '../../exercise-templates/matchingConfig';
import { MatchingLines, MatchingChipContent } from '../../courses/preview/interactivePreviews';
import { renderRichText } from '../../../utils/richText';
import { resolveCorrectPositions, correctItemBySlot } from '../../exercise-templates/orderingConfig';

// Read-only "what did the student actually answer" renderers - separate
// from courses/preview/interactivePreviews.jsx's components, which are
// LIVE local-state-only simulators for admins previewing content (no way
// to feed them a prior answer). These instead parse the real
// ExerciseStudentSavedInteractiveElement.resultJson and render it as
// already-decided state, reusing the exact same CSS classes/markup shape
// as the admin preview (and, by extension, the student platform, which
// ported that same markup) so a "picked" option looks identical to how it
// looks live - just frozen, with correct/incorrect coloring added.
//
// resultJson shapes below are verified against english-app's own save
// calls (the only thing that ever writes these rows), not guessed:
//   multiple_choice: {picked: [id,...]}
//   true_false:      {picked: true|false|null}
//   fill_blank:       {values: {"0":"text",...}}  (keyed by blank index)
//   matching:         {matched: [pairId,...], matchColors: {pairId: {solid,bg,glow}}}
//                     (mode 'deferred': {connections: [{left,right},...]}, pair indices, correct iff left===right)
//   ordering:         {placed: [itemIndex|null, ...]}  (index = slot, value = index into config.items)
//
// Crucially, english-app itself is NEVER told the answer key for
// multiple_choice/true_false/fill_blank (backend withholds correctIds/
// correct/blanks.accepted from the student-facing API on purpose), so it
// can only highlight "picked", never right/wrong - the CRM teacher view
// has no such restriction (full config access), so correct/wrong coloring
// here is a deliberate CRM-side addition beyond what the platform itself
// can show, computed against the same #30e846/#ff5140 green/red convention
// already established elsewhere in this preview system for consistency.

function safeParseResult(resultJson, fallback) {
   if (!resultJson) return fallback;
   try {
      const parsed = JSON.parse(resultJson);
      return parsed && typeof parsed === 'object' ? parsed : fallback;
   } catch (e) {
      return fallback;
   }
}

const MC_DEFAULT = { question: '', options: [], correctIds: [], multiple: false };
export function AnsweredMultipleChoice({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, MC_DEFAULT);
   const options = (cfg.options || []).filter(o => o.text && o.text.trim());
   const result = safeParseResult(resultJson, { picked: [] });
   const picked = (result.picked || []).map(String);
   const correctIds = (cfg.correctIds || []).map(String);

   return (
      <div className='course-preview-question'>
         {cfg.question ? <p className='course-preview-question-text'>{cfg.question}</p> : ''}
         <div className='course-preview-options'>
            {options.map(o => {
               const isPicked = picked.includes(String(o.id));
               const isCorrectOption = correctIds.includes(String(o.id));
               const cls = 'course-preview-option-row'
                  + (isPicked && isCorrectOption ? ' correct' : '')
                  + (isPicked && !isCorrectOption ? ' wrong' : '')
                  + (!isPicked && isCorrectOption ? ' correct-hint' : '');
               return (
                  <label className={cls} key={o.id}>
                     <input type={cfg.multiple ? 'checkbox' : 'radio'} checked={isPicked} readOnly disabled />
                     <span>{o.text}</span>
                     {!isPicked && isCorrectOption ? <em className='workspace-answered-correct-tag'>правильна відповідь</em> : ''}
                  </label>
               );
            })}
         </div>
      </div>
   );
}

const TF_DEFAULT = { statement: '', correct: true };
export function AnsweredTrueFalse({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, TF_DEFAULT);
   const result = safeParseResult(resultJson, { picked: null });
   const picked = result.picked;

   const rows = [
      { value: true, label: 'Правда' },
      { value: false, label: 'Хибність' },
   ];

   return (
      <div className='course-preview-question'>
         {cfg.statement ? <p className='course-preview-question-text'>{cfg.statement}</p> : ''}
         <div className='course-preview-options'>
            {rows.map(row => {
               const isPicked = picked === row.value;
               const isCorrectOption = cfg.correct === row.value;
               const cls = 'course-preview-option-row'
                  + (isPicked && isCorrectOption ? ' correct' : '')
                  + (isPicked && !isCorrectOption ? ' wrong' : '')
                  + (!isPicked && isCorrectOption ? ' correct-hint' : '');
               return (
                  <label className={cls} key={row.label}>
                     <input type='radio' checked={isPicked} readOnly disabled />
                     <span>{row.label}</span>
                     {!isPicked && isCorrectOption ? <em className='workspace-answered-correct-tag'>правильна відповідь</em> : ''}
                  </label>
               );
            })}
         </div>
      </div>
   );
}

const FB_DEFAULT = { template: '', blanks: [] };
const BLANK_MARKER = '___';
export function AnsweredFillBlank({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, FB_DEFAULT);
   const parts = (cfg.template || '').split(BLANK_MARKER);
   const result = safeParseResult(resultJson, { values: {} });
   const values = result.values || {};
   const blanks = cfg.blanks || [];

   return (
      <p className='course-preview-question-text course-preview-fill-blank'>
         {parts.map((part, i) => {
            if (i === parts.length - 1) return <span key={i}>{part}</span>;
            const value = values[String(i)] || '';
            const accepted = (blanks[i] && blanks[i].accepted) || [];
            const isCorrect = value.trim() !== '' && accepted.some(a => a.trim().toLowerCase() === value.trim().toLowerCase());
            return (
               <span key={i}>
                  {part}
                  <span className={'course-preview-blank-input workspace-answered-blank' + (value ? (isCorrect ? ' correct' : ' wrong') : ' empty')}>
                     {value || '—'}
                  </span>
               </span>
            );
         })}
      </p>
   );
}

// Same SVG-connecting-line technique as the live MatchingPreview (reused
// via MatchingLines/MatchingChipContent, exported from interactivePreviews
// for exactly this reuse) - but fed the STUDENT'S saved matched pairs
// instead of live click state, and no shuffle (nothing to hide here, and
// shuffling would need a stable seed to avoid re-shuffling every render for
// no benefit). resultJson.matched only ever contains CORRECT matches - the
// platform never persists a wrong attempt (only a transient 400ms flash,
// never saved) - so every matched chip shown here is a real correct pair
// the student made; anything left unconnected simply wasn't matched yet.
function AnsweredLiveMatching({ cfg, resultJson }) {
   const mediaWidth = cfg.mediaWidth || MATCHING_DEFAULT_CONFIG.mediaWidth;
   const mediaHeight = cfg.mediaHeight || MATCHING_DEFAULT_CONFIG.mediaHeight;
   const pairs = useMemo(() => normalizePairs(cfg.pairs)
      .map((p, i) => ({ ...p, id: i }))
      .filter(p => isSideFilled(p.left) || isSideFilled(p.right)), [cfg.pairs]);
   const result = safeParseResult(resultJson, { matched: [], matchColors: {} });
   const matched = result.matched || [];
   const matchColors = result.matchColors || {};
   const containerRef = useRef(null);
   const leftRefs = useRef({});
   const rightRefs = useRef({});

   if (pairs.length === 0) return null;

   const chipStyle = (id) => {
      const color = matchColors[id];
      return color ? { '--match-solid': color.solid, '--match-bg': color.bg } : undefined;
   };

   return (
      <div className='course-preview-matching-game' ref={containerRef}>
         <MatchingLines matched={matched} matchColors={matchColors} containerRef={containerRef} leftRefs={leftRefs} rightRefs={rightRefs} />
         <div className='course-preview-matching-game-col'>
            {pairs.map(p => (
               <div key={p.id} ref={el => { leftRefs.current[p.id] = el; }} style={chipStyle(p.id)}
                  className={'course-preview-matching-chip' + (matched.includes(p.id) ? ' matched' : '')}>
                  <MatchingChipContent side={p.left} mediaWidth={mediaWidth} mediaHeight={mediaHeight} />
               </div>
            ))}
         </div>
         <div className='course-preview-matching-game-col'>
            {pairs.map(p => (
               <div key={p.id} ref={el => { rightRefs.current[p.id] = el; }} style={chipStyle(p.id)}
                  className={'course-preview-matching-chip' + (matched.includes(p.id) ? ' matched' : '')}>
                  <MatchingChipContent side={p.right} mediaWidth={mediaWidth} mediaHeight={mediaHeight} />
               </div>
            ))}
         </div>
      </div>
   );
}

const CONNECTION_CORRECT = { solid: '#30e846', bg: 'rgba(48, 232, 70, 0.14)', glow: 'rgba(48, 232, 70, 0.6)' };
const CONNECTION_WRONG = { solid: '#ff5140', bg: 'rgba(255, 81, 64, 0.14)', glow: 'rgba(255, 81, 64, 0.6)' };

// mode 'deferred': the student was free to join ANY left to ANY right, so
// (unlike 'live', which only ever saves correct pairs) resultJson holds real
// wrong attempts too - {connections:[{left,right}]}, pair indices into
// config.pairs. Judged here per connection (left === right => correct) with
// the same green/red convention, lines and chips both colored.
function AnsweredDeferredMatching({ cfg, resultJson }) {
   const mediaWidth = cfg.mediaWidth || MATCHING_DEFAULT_CONFIG.mediaWidth;
   const mediaHeight = cfg.mediaHeight || MATCHING_DEFAULT_CONFIG.mediaHeight;
   const pairs = useMemo(() => normalizePairs(cfg.pairs)
      .map((p, i) => ({ ...p, id: i }))
      .filter(p => isSideFilled(p.left) || isSideFilled(p.right)), [cfg.pairs]);
   const result = safeParseResult(resultJson, { connections: [] });
   const connections = useMemo(() => (Array.isArray(result.connections) ? result.connections : [])
      .filter(c => c && c.left != null && c.right != null)
      .map(c => ({ key: c.left + '-' + c.right, left: c.left, right: c.right, color: c.left === c.right ? CONNECTION_CORRECT : CONNECTION_WRONG })),
   // eslint-disable-next-line react-hooks/exhaustive-deps
   [resultJson]);
   const containerRef = useRef(null);
   const leftRefs = useRef({});
   const rightRefs = useRef({});

   if (pairs.length === 0) return null;

   const styleFor = (side, id) => {
      const c = connections.find(x => x[side] === id);
      return c ? { '--match-solid': c.color.solid, '--match-bg': c.color.bg } : undefined;
   };
   const classFor = (side, id) => 'course-preview-matching-chip' + (connections.some(x => x[side] === id) ? ' matched' : '');

   return (
      <div>
         <div className='course-preview-matching-game' ref={containerRef}>
            <MatchingLines connections={connections} containerRef={containerRef} leftRefs={leftRefs} rightRefs={rightRefs} />
            <div className='course-preview-matching-game-col'>
               {pairs.map(p => (
                  <div key={p.id} ref={el => { leftRefs.current[p.id] = el; }} style={styleFor('left', p.id)} className={classFor('left', p.id)}>
                     <MatchingChipContent side={p.left} mediaWidth={mediaWidth} mediaHeight={mediaHeight} />
                  </div>
               ))}
            </div>
            <div className='course-preview-matching-game-col'>
               {pairs.map(p => (
                  <div key={p.id} ref={el => { rightRefs.current[p.id] = el; }} style={styleFor('right', p.id)} className={classFor('right', p.id)}>
                     <MatchingChipContent side={p.right} mediaWidth={mediaWidth} mediaHeight={mediaHeight} />
                  </div>
               ))}
            </div>
         </div>
         {connections.length === 0 ? <p className='workspace-activity-empty-hint'>Студент ще нічого не з’єднав.</p> : ''}
      </div>
   );
}

export function AnsweredMatching({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, MATCHING_DEFAULT_CONFIG);
   if (getMatchingMode(cfg) === MATCHING_MODE_DEFERRED) return <AnsweredDeferredMatching cfg={cfg} resultJson={resultJson} />;
   return <AnsweredLiveMatching cfg={cfg} resultJson={resultJson} />;
}

// Free-text response - read-only, a plain styled block (deliberately NOT a
// <textarea>, disabled or otherwise) showing exactly what the student
// wrote, matching this page's "never even look like an editable form"
// stance for anything answerable. resultJson shape: {text:"..."} - the
// student platform is the one writing this (per the same generic
// ExerciseStudentSavedInteractiveElement save path every other type uses),
// no correctness concept here since it's an open-ended written answer, not
// a graded one.
const ESSAY_DEFAULT = { prompt: '' };
export function AnsweredEssay({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, ESSAY_DEFAULT);
   const result = safeParseResult(resultJson, { text: '' });
   const text = (result.text || '').trim();

   return (
      <div className='course-preview-essay'>
         {cfg.prompt ? <p className='course-preview-question-text'>{cfg.prompt}</p> : ''}
         {text ? (
            <div className='workspace-answered-essay-text'>{text}</div>
         ) : (
            <p className='workspace-activity-empty-hint'>Студент ще не написав відповідь.</p>
         )}
      </div>
   );
}

// Audio response - identical {prompt} config to essay, resultJson
// {fileId:number}: a bare id into the existing admin `File` entity (the
// platform uploads via the same reusable MediaStorageService every other
// media owner in this backend already uses, category/owner TBD on that
// side). Played back directly through the existing admin-only
// GET /api/files/{id}/content - the teacher's own browser session already
// carries the right cookie, no new read endpoint needed.
export function AnsweredAudioResponse({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, ESSAY_DEFAULT);
   const result = safeParseResult(resultJson, { fileId: null });

   return (
      <div className='course-preview-audio-response'>
         {cfg.prompt ? <p className='course-preview-question-text'>{cfg.prompt}</p> : ''}
         {result.fileId ? (
            <audio className='workspace-answered-audio-player' controls src={'/api/files/' + result.fileId + '/content'} />
         ) : (
            <p className='workspace-activity-empty-hint'>Студент ще не записав відповідь.</p>
         )}
      </div>
   );
}

// Sentence-order - resultJson {words:string[]}, the student's own actual
// submitted order (plain strings, not indices - simplest possible shape
// for the platform to record, "whatever you ended up with"). Graded
// position-by-position against config.words, same green/red convention as
// everywhere else; the correct sentence is also shown for reference below
// so a wrong attempt is still legible at a glance.
const SENTENCE_ORDER_DEFAULT = { prompt: '', words: [] };
export function AnsweredSentenceOrder({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, SENTENCE_ORDER_DEFAULT);
   const result = safeParseResult(resultJson, { words: [] });
   const studentWords = result.words || [];

   return (
      <div className='course-preview-sentence-order'>
         {cfg.prompt ? <p className='course-preview-question-text'>{cfg.prompt}</p> : ''}
         {studentWords.length === 0 ? (
            <p className='workspace-activity-empty-hint'>Студент ще не склав речення.</p>
         ) : (
            <>
               <div className='course-preview-sentence-build-area'>
                  {studentWords.map((w, i) => (
                     <span key={i} className={'course-preview-word-chip built' + (cfg.words[i] === w ? ' correct' : ' wrong')}>{w}</span>
                  ))}
               </div>
               <p className='workspace-answered-reference-sentence'>Правильно: {cfg.words.join(' ')}</p>
            </>
         )}
      </div>
   );
}

// Ordering - resultJson {placed:(number|null)[]}: placed[slot] is the index
// into config.items of the item the student put in that (0-based) slot, or
// null if the slot was left empty. Graded per slot against
// config.correctPositions (absent -> array order is the key, see
// orderingConfig.js), same green/red chips as sentence_order; the correct
// order is listed underneath so a wrong attempt is still legible.
const ORDERING_DEFAULT = { prompt: '', items: [], correctPositions: [] };
export function AnsweredOrdering({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, ORDERING_DEFAULT);
   const items = cfg.items || [];
   const positions = resolveCorrectPositions(items, cfg.correctPositions);
   const correctBySlot = correctItemBySlot(positions);
   const result = safeParseResult(resultJson, { placed: [] });
   const placed = Array.isArray(result.placed) ? result.placed : [];

   if (items.length === 0) return null;

   return (
      <div className='course-preview-question'>
         {cfg.prompt ? <p className='course-preview-question-text'>{renderRichText(cfg.prompt)}</p> : ''}
         {placed.every(x => x == null) ? (
            <p className='workspace-activity-empty-hint'>Студент ще не розташував елементи.</p>
         ) : (
            <>
               <div className='course-preview-ordering-slots'>
                  {items.map((_, s) => {
                     const itemIdx = placed[s];
                     const has = itemIdx != null && items[itemIdx] != null;
                     const cls = 'course-preview-word-chip course-preview-ordering-chip' + (has ? (correctBySlot[s] === itemIdx ? ' correct' : ' wrong') : '');
                     return (
                        <div className='course-preview-ordering-slot-row' key={s}>
                           <span className='course-preview-ordering-slot-number'>{s + 1}</span>
                           <div className='course-preview-ordering-slot'>
                              {has ? <span className={cls}>{renderRichText(items[itemIdx])}</span> : <span className='workspace-activity-empty-hint'>—</span>}
                           </div>
                        </div>
                     );
                  })}
               </div>
               <p className='workspace-answered-reference-sentence'>
                  Правильно: {correctBySlot.map((itemIdx, i) => (i + 1) + '. ' + (items[itemIdx] || '')).join('  ')}
               </p>
            </>
         )}
      </div>
   );
}

// Error-spotting - resultJson {pickedWordIndex:number}. Same "picked +
// correct-hint" 3-state coloring already established for multiple_choice/
// true_false, applied to word spans instead of option rows.
const ERROR_SPOTTING_DEFAULT = { words: [], errorWordIndex: 0 };
export function AnsweredErrorSpotting({ block, resultJson }) {
   const cfg = safeParseConfig(block.config, ERROR_SPOTTING_DEFAULT);
   const result = safeParseResult(resultJson, { pickedWordIndex: null });
   const picked = result.pickedWordIndex;

   return (
      <div className='course-preview-error-spotting'>
         <p className='course-preview-question-text'>Знайдіть слово з помилкою:</p>
         {picked == null ? (
            <p className='workspace-activity-empty-hint'>Студент ще не вибрав слово.</p>
         ) : (
            <p className='course-preview-error-spotting-sentence'>
               {cfg.words.map((w, i) => {
                  const isPicked = i === picked;
                  const isActualError = i === cfg.errorWordIndex;
                  const cls = 'course-preview-error-word'
                     + (isPicked && isActualError ? ' correct' : '')
                     + (isPicked && !isActualError ? ' wrong' : '')
                     + (!isPicked && isActualError ? ' correct-hint' : '');
                  return <span key={i} className={cls}>{w} </span>;
               })}
            </p>
         )}
      </div>
   );
}

const ANSWERED_COMPONENTS = {
   multiple_choice: AnsweredMultipleChoice,
   true_false: AnsweredTrueFalse,
   fill_blank: AnsweredFillBlank,
   matching: AnsweredMatching,
   essay: AnsweredEssay,
   audio_response: AnsweredAudioResponse,
   sentence_order: AnsweredSentenceOrder,
   ordering: AnsweredOrdering,
   error_spotting: AnsweredErrorSpotting,
};

export function getAnsweredComponent(type) {
   return ANSWERED_COMPONENTS[type] || null;
}
